// Around Town, a loop screen since Oct 26: up to four pages of 20 s. 1) tonight and tomorrow; 2) this week; 3) for
// families, when there are a couple of one-off kid and family events; 4) the teams, home games marked. Rows never
// wrap: title and venue share one line, cut at a word, the tag apart.
// Selection (Oct 26, Jared: "a random assortment"): a consensus of the week's biggest things. Each event scores on how
// many editors' lists pick it (SF Standard's weekly best events, 48 Hills' Big Week), DoTheBay's votes, a big room,
// and FunCheap's annual and festival marks; promos, sponsored posts and anything that recurs (weekly nights, After
// Dark, food trucks) are left out, since the audience lives here; a small club shows once a page, a big room twice.
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import FeatureDisplay from './feature-display.mjs';
import { data, fetchedAt } from './snapshot.mjs';
import {
	asOf, dayKey, time, weekday, isToday,
} from './format.mjs';

const ROWS = 6;
const DAY = 86400000;

// "8pm", "10:30pm", "noon" → minutes after midnight; early-morning hours count as the night before's
const minutes = (text) => {
	if (!text) return null;
	if (/noon/i.test(text)) return 12 * 60;
	const match = text.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
	if (!match) return null;
	let hour = Number(match[1]) % 12;
	if (match[3].toLowerCase() === 'pm') hour += 12;
	const total = hour * 60 + Number(match[2] ?? 0);
	return hour < 5 ? total + 24 * 60 : total;
};
const nowMinutes = (now) => {
	const [h, m] = new Intl.DateTimeFormat('en-GB', {
		timeZone: window.RHL.clock.timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
	}).format(now).split(':').map(Number);
	return h * 60 + m;
};

const DAY_KEY = (offset, now) => dayKey(now.getTime() + offset * DAY);
// FunCheap's titles carry "(Every Monday)", "(SF)" and the like
const clean = (title = '') => title.replace(/\s*\((?:every [^)]*|sf|san francisco|free[^)]*)\)/gi, '').replace(/\s*\|.*$/, '').replace(/\s+/g, ' ').trim();
const norm = (title) => clean(title).toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\b(presents|w|with|the)\b/g, '').replace(/\s+/g, ' ').trim().slice(0, 24);
// cut at a word (the Star4000 font has no ellipsis)
const cut = (text, n) => (text.length <= n ? text : text.slice(0, n).replace(/\s+\S*$/, ''));
const fit = (title, venue, max = 40) => {
	const t = clean(title);
	if (venue) {
		const room = max - venue.length - 3;
		if (room >= 18) return `${escapeHtml(cut(t, room))} &middot; ${escapeHtml(venue)}`;
	}
	return escapeHtml(cut(t, max));
};
const BIG_ROOMS = /chase center|oracle park|fillmore|warfield|masonic|castro|bill graham|great american music hall|bimbo|cobb|august hall|regency|orpheum|golden gate theatre|curran|davies|war memorial|opera house|sfjazz|independent|palace of fine arts|sydney goldstein|herbst|punch line|kezar|cow palace|frost amphitheater|greek theatre|yerba buena|ybca|presidio theatre|de young|legion of honor|exploratorium|academy of sciences/i;
// things a local doesn't need flagged: standing weekly nights and series, and promos
const RECURRING = /\b(every|weekly|nightly|daily|mondays|tuesdays|wednesdays|thursdays|fridays|saturdays|sundays|happy hour|trivia|open mic|karaoke|industry night|after dark|nightlife|food trucks?|off the grid|sound baths?|comedy night|game night|bingo|drag brunch|storytime)\b/i;
const PROMO = /% off|\$\d+\s*(drinks?|margaritas?|tacos?|beers?|wings|oysters|shots?|mimosas?|wine)|drink specials?|\bdeals?\b|discount|\bbogo\b|free game tokens|bottomless|prix fixe|\bpromo\b|real estate|realtor/i;
const FAMILY = /\b(kids?|family|families|children|child|storytime|trick.or.treat|puppet|zoo|aquarium|toddlers?|teens?|all ages|petting)\b/i;
const STOP_WORDS = new Set('the and with for from presents feat featuring live tour show night at of a an in on to sf san francisco w'.split(' '));
const words = (title) => clean(title).toLowerCase().replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter((w) => w.length > 2 && !STOP_WORDS.has(w));
// the same event in two lists: one title's words all appear in the other's (a lone word must be a long one), on a
// compatible date
const sameEvent = (a, b) => {
	if (a.date && b.date && a.date !== b.date && !(b.to && a.date >= b.date && a.date <= b.to)) return false;
	const [x, y] = [words(a.title), words(b.title)].sort((p, q) => p.length - q.length);
	if (!x.length || (x.length === 1 && x[0].length < 5)) return false;
	const set = new Set(y);
	return x.every((w) => set.has(w));
};
const isRecurring = (e) => e.recurring || RECURRING.test(e.title ?? '');
const isPromo = (e) => PROMO.test(e.title ?? '');

// every candidate for the week, from DoTheBay and FunCheap, with the editors' picks folded in; an editor's pick that
// no listing carries comes in on its own
const candidates = (events, now) => {
	const today = DAY_KEY(0, now);
	const last = DAY_KEY(7, now);
	const picks = [
		...(events?.standard?.picks ?? []).map((p) => ({ ...p, editor: 'SF Std' })),
		...(events?.hills?.picks ?? []).map((p) => ({ title: p.title, date: p.from, to: p.to, editor: '48 Hills' })),
	].filter((p) => !isRecurring(p) && !isPromo(p) && (!p.date || ((p.to ?? p.date) >= today && p.date <= last)));
	// DoTheBay titles that come back three or more days in the week are a series, unless they're in a big room
	const runs = new Map();
	(events?.dothebay ?? []).forEach((e) => runs.set(norm(e.title), (runs.get(norm(e.title)) ?? new Set()).add(e.date)));
	const listed = [
		...(events?.dothebay ?? []).filter((e) => !(runs.get(norm(e.title)).size >= 3 && !e.ongoing && !BIG_ROOMS.test(e.venue ?? ''))),
		...(events?.listings ?? []),
	].filter((e) => e.date >= today && e.date <= last && !isRecurring(e) && !isPromo(e));
	// one row per event: the first listing of a title on a date wins (DoTheBay comes first, with the venue)
	const seen = new Set();
	const out = [];
	listed.forEach((e) => {
		const key = `${e.ongoing ? 'run' : e.date} ${norm(e.title)}`;
		if (seen.has(key)) return;
		seen.add(key);
		const editors = [...new Set(picks.filter((p) => sameEvent(e, p)).map((p) => p.editor))];
		out.push({ ...e, editors });
	});
	picks.forEach((p) => {
		if (out.some((e) => sameEvent(e, p))) return;
		const twin = out.find((e) => e.fromPick && sameEvent(e, p));
		if (twin) {
			if (!twin.editors.includes(p.editor)) twin.editors.push(p.editor);
			return;
		}
		out.push({
			title: p.title, date: p.date ?? today, to: p.to, time: null, venue: null, editors: [p.editor], fromPick: true,
		});
	});
	return out;
};
// the consensus: editors' picks count most, then DoTheBay's votes, a big room, an annual or festival mark, nearness
const score = (e) => (e.editors?.length ?? 0) * 40
	+ Math.min(30, (e.votes ?? 0) / 15)
	+ (BIG_ROOMS.test(e.venue ?? '') ? 25 : 0)
	+ (e.annual ? 15 : 0)
	+ (e.near === 'near' ? 6 : e.near === 'close' ? 3 : 0);
// a small room once a page, a big room twice
const capVenues = (list, n) => {
	const used = new Map();
	return list.filter((e) => {
		if (!e.venue) return true;
		const key = e.venue.toLowerCase();
		const max = BIG_ROOMS.test(e.venue) ? 2 : 1;
		if ((used.get(key) ?? 0) >= max) return false;
		used.set(key, (used.get(key) ?? 0) + 1);
		return true;
	}).slice(0, n);
};
const editorLabel = (e) => {
	if (!e.editors?.length) return null;
	return e.editors.length > 1 ? { label: `${e.editors.length} picks`, labelClass: 'pick' } : { label: e.editors[0], labelClass: 'info' };
};

// the right-hand column (Oct 26, Jared: "SOLD" said too little): sold out in full, else the price or Free, else
// tickets going on sale today, else the age limit, else the kind of show. DoTheBay sends no prices.
const CATEGORY = {
	music: 'Music', comedy: 'Comedy', film: 'Film', movies: 'Film', theater: 'Theater', theatre: 'Theater', arts: 'Art', art: 'Art',
	'food & drink': 'Food', food: 'Food', nightlife: 'Night', party: 'Party', sports: 'Sports', talks: 'Talk', variety: 'Variety', dance: 'Dance',
};
const tag = (e) => {
	if (e.soldOut || e.price === 'SOLD') return { text: 'Sold out', cls: 'sold' };
	const price = String(e.price ?? '').trim();
	if (/^free$/i.test(price)) return { text: 'Free', cls: 'free' };
	if (price) return { text: price.length > 8 ? price.slice(0, 8) : price, cls: '' };
	if (e.onSaleToday) return { text: 'On sale', cls: '' };
	if (e.age) return { text: e.age, cls: 'info' };
	const kind = CATEGORY[String(e.category ?? '').toLowerCase()];
	return kind ? { text: kind, cls: 'info' } : { text: '', cls: '' };
};

// one row: when, what and where, and the tag
const row = ({ when, title, venue, near, ...e }) => {
	const text = fit(title, venue, 37);
	const { text: label, cls } = e.label ? { text: e.label, cls: e.labelClass ?? '' } : tag(e);
	return `<div class="row ev${near ? ' near' : ''}"><div class="t">${escapeHtml(when)}</div><div>${text}</div><div class="p ${cls}">${escapeHtml(label)}</div></div>`;
};

const minutesOf = (e) => minutes(e.time) ?? 18 * 60;
const dayTag = (date, now) => (date === DAY_KEY(0, now) ? '' : `${weekday(new Date(`${date}T12:00:00`))} `);

const byWhen = (x, y) => (x.date === y.date ? minutesOf(x) - minutesOf(y) : x.date < y.date ? -1 : 1);
const asRow = (e, when) => ({
	when, title: e.title, venue: e.venue, price: e.price, soldOut: e.soldOut, age: e.age, onSaleToday: e.onSaleToday, category: e.category, near: e.near === 'near', ...(editorLabel(e) ?? {}),
});

// page 1: tonight (what hasn't long started) and tomorrow, the top of the consensus
const tonightRows = (pool, now) => {
	const today = DAY_KEY(0, now);
	const tomorrow = DAY_KEY(1, now);
	const current = nowMinutes(now);
	const fresh = (e) => e.date !== today || e.fromPick || minutesOf(e) >= current - 90;
	const list = pool.filter((e) => (e.date === today || e.date === tomorrow) && fresh(e)).sort((x, y) => score(y) - score(x));
	return capVenues(list, ROWS).sort(byWhen).map((e) => asRow(e, `${e.date === tomorrow ? `${weekday(now.getTime() + DAY)} ` : ''}${e.time ?? ''}`.trim() || '--'));
};

// page 2: the rest of the week, editors' picks and big rooms first (a run shows once, as "thru")
const weekRows = (pool, now) => {
	const start = DAY_KEY(2, now);
	const end = DAY_KEY(7, now);
	const list = pool.filter((e) => e.date >= start && e.date <= end).sort((x, y) => score(y) - score(x));
	return capVenues(list, ROWS).sort(byWhen).map((e) => {
		const when = e.to && e.to > e.date ? `thru ${weekday(new Date(`${e.to}T12:00:00`))}` : `${dayTag(e.date, now)}${e.time ?? ''}`.trim();
		return asRow(e, when || weekday(new Date(`${e.date}T12:00:00`)));
	});
};

// page 3: one-off kid and family events this week (FunCheap's kids-and-families mark, or a family word in the title)
const familyRows = (pool, now) => {
	const list = pool.filter((e) => e.kids || FAMILY.test(e.title ?? ''))
		.sort((x, y) => byWhen(x, y) || score(y) - score(x));
	return capVenues(list, ROWS).map((e) => asRow(e, `${dayTag(e.date, now) || 'Today '}${e.time ?? ''}`.trim()));
};

const gameRow = (game, now) => {
	const t = new Date(game.t);
	const when = isToday(t, now) ? time(t) : `${weekday(t)} ${time(t)}`;
	return row({
		when, title: `${game.team}: ${game.name}`, venue: game.atHome ? game.venue : null, label: game.atHome ? 'Home' : '', near: false,
	});
};

class AroundTown extends FeatureDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Around Town');
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		const events = data('events');
		return !!(events?.listings?.length || events?.dothebay?.length || data('sports')?.length);
	}

	// eslint-disable-next-line class-methods-use-this
	buildPages(now) {
		const events = data('events');
		const pool = candidates(events, now);
		const pages = [];
		const sources = ['SF Standard', '48 Hills', 'DoTheBay', 'FunCheap'].filter((x, i) => [events?.standard, events?.hills, events?.dothebay?.length, events?.listings?.length][i]).join(' &middot; ');
		const tonight = tonightRows(pool, now);
		if (tonight.length) {
			pages.push({
				seconds: 20,
				title: ['Around Town', `Tonight & ${weekday(now.getTime() + DAY)}`],
				html: `<div class="hdr">Tonight &amp; tomorrow</div>
					<div class="list events">${tonight.map(row).join('')}</div>
					<div class="asof">${sources} &middot; ${asOf(fetchedAt('events'), now)}</div>`,
			});
		}
		const week = weekRows(pool, now);
		if (week.length) {
			pages.push({
				seconds: 20,
				title: ['Around Town', 'This Week'],
				html: `<div class="hdr">This week &middot; the editors' picks first</div>
					<div class="list events">${week.map(row).join('')}</div>
					<div class="asof">${sources}</div>`,
			});
		}
		const family = familyRows(pool, now);
		if (family.length >= 2) {
			pages.push({
				seconds: 20,
				title: ['Around Town', 'For Families'],
				html: `<div class="hdr">For families &middot; this week</div>
					<div class="list events">${family.map(row).join('')}</div>
					<div class="asof">FunCheap &middot; ${sources}</div>`,
			});
		}
		const games = (data('sports') ?? [])
			.filter((g) => new Date(g.t) - now < 7 * DAY && new Date(g.t) > now - 3 * 3600000)
			.sort((a, b) => new Date(a.t) - new Date(b.t))
			.slice(0, ROWS);
		if (games.length) {
			pages.push({
				seconds: 20,
				title: ['Around Town', 'The Teams'],
				html: `<div class="hdr">Next games</div><div class="list events games">${games.map((g) => gameRow(g, now)).join('')}</div>
					<div class="asof">via ESPN</div>`,
			});
		}
		return pages;
	}
}

const display = new AroundTown(23, 'around-town');
display.makeLoopScreen();
registerDisplay(display);
